import React from 'react';
import { applyStyles } from 'util/helpers';
import { Form, Field } from 'react-final-form';
import { css } from 'styled-components';
import CustomMedia from 'util/custom-media-query';
import Jump from 'components/common/Jump';
import i18n from 'i18n';
import Button from 'components/common/Button';
import Breadcrumbs from 'components/common/Breadcrumbs';
import GrayHeader from 'components/common/GrayHeader';
import Colors from 'constants/colors';
import FontStyles from 'constants/font-styles';
import TextField from 'components/common/TextField';
import Tag from 'components/common/Tag';
import FormError from 'components/common/FormError';
import {
  required,
  mustBeNumber,
  emailPattern,
  composeValidators
} from 'validation/validators';
import Completion from './Completion';
import {
  replaceHyphenAndSpaces,
  convertDoubleWidthNumber,
  allowExactKeysOnly,
  convertDoubleWidthAlpha,
  convertDoubleWidthSymbol
} from 'util/helpers';

class Contact extends React.Component {
  constructor(props) {
    super(props);

    this.initialValues = {
      company: '',
      name: '',
      telephone: '',
      email: '',
      description: ''
    };
  }

  componentDidUpdate(prevProps) {
    // エラー時の処理
    if (this.props.error && prevProps.error == null) {
      const {
        showAlert,
        error: {
          data: { message }
        }
      } = this.props;
      showAlert(message);
    }
  }

  onSubmit = async data => {
    const { postContact } = this.props;
    await postContact(data);
  };

  handleBlurNumbers = (change, value) => {
    const replaced = convertDoubleWidthNumber(value);
    const formatted = replaceHyphenAndSpaces(replaced);

    change('telephone', formatted);
  };

  handleBlurEmail = (change, value) => {
    const replacedNumber = convertDoubleWidthNumber(value);
    const replacedAlpha = convertDoubleWidthAlpha(replacedNumber);
    const replaced = convertDoubleWidthSymbol(replacedAlpha);

    change('email', replaced);
  };

  render() {
    const {
      className,
      location: { pathname },
      push,
      showContactComplete
    } = this.props;

    return (
      <div className={className}>
        <GrayHeader text={i18n.t('contact:grayHeader')} />
        <Breadcrumbs currentPath={pathname} />
        <Jump />
        {showContactComplete ? (
          <Completion push={push} />
        ) : (
          <React.Fragment>
            <p className="overview">{i18n.t('contact:overview')}</p>
            <Form
              onSubmit={this.onSubmit}
              initialValues={this.initialValues}
              render={({
                handleSubmit,
                pristine,
                hasValidationErrors,
                submitting,
                form: { change }
              }) => (
                <form onSubmit={handleSubmit}>
                  <TextField
                    name="company"
                    label={i18n.t('contact:company:title')}
                    placeholder={i18n.t('contact:company:placeholder')}
                    maxLength={20}
                    validate={value => required(value)}
                    required={true}
                  />
                  <TextField
                    name="name"
                    label={i18n.t('contact:name:title')}
                    placeholder={i18n.t('contact:name:placeholder')}
                    maxLength={20}
                    validate={value => required(value)}
                    required={true}
                  />
                  <TextField
                    type="tel"
                    name="telephone"
                    label={i18n.t('contact:telephone:title')}
                    placeholder={i18n.t('contact:telephone:placeholder')}
                    autoComplete="tel"
                    maxLength={11}
                    validate={value =>
                      composeValidators(required, mustBeNumber)(value)
                    }
                    required={true}
                    onBlur={value => this.handleBlurNumbers(change, value)}
                    onKeyDown={event => allowExactKeysOnly(event, /[^-]/)}
                  />
                  <TextField
                    name="email"
                    label={i18n.t('contact:email:title')}
                    placeholder={i18n.t('contact:email:placeholder')}
                    autoComplete="email"
                    maxLength={255}
                    validate={value =>
                      composeValidators(required, emailPattern)(value)
                    }
                    required={true}
                    onBlur={value => this.handleBlurEmail(change, value)}
                  />
                  <Field
                    name="description"
                    validate={value => required(value)}
                    render={({ input, meta }) => (
                      <div className="description">
                        <label htmlFor="description">
                          <span>{i18n.t('contact:description:title')}</span>
                          <Tag>{i18n.t('common:required')}</Tag>
                        </label>
                        <textarea
                          {...input}
                          className={
                            meta.error && meta.touched ? 'has-error' : ''
                          }
                          maxLength={2000}
                          placeholder={i18n.t(
                            'contact:description:placeholder'
                          )}
                        />

                        <FormError {...meta} />
                      </div>
                    )}
                  />
                  <div className="submit-btn-wrapper">
                    <Button
                      type="submit"
                      buttonType="B"
                      disabled={pristine || hasValidationErrors || submitting}
                    >
                      {i18n.t('contact:submit')}
                    </Button>
                  </div>
                </form>
              )}
            />
          </React.Fragment>
        )}
      </div>
    );
  }
}

const commonStyle = css`
  width: 100%;
  height: 100%;
  margin: 0 auto;

  .overview {
    white-space: pre-line;
  }

  .description {
    label {
      ${FontStyles.text5}

      display: flex;
      color: ${Colors.black1};
      align-items: center;

      span {
        display: block;
      }
    }

    textarea {
      width: 100%;
      background-color: ${Colors.white1};
      color: ${Colors.black1};
      padding: 12px 8px;
      border: 0;
      border-radius: 4px;
      box-shadow: inset 0 0 0 1px ${Colors.gray2};
      outline: none;
      resize: none;

      // safari でのみ文字が折り返されるため
      word-break: normal;

      &::placeholder {
        color: ${Colors.gray2};
        opacity: 1;
      }

      &.has-error {
        background-color: ${Colors.orange2};
        box-shadow: inset 0 0 0 4px ${Colors.orange1};
      }
    }
  }

  .submit-btn-wrapper {
    text-align: center;
  }
`;

const pcStyle = CustomMedia.greaterThan('umedium')`
  max-width: 886px;
  padding-bottom: 160px;

  ${Breadcrumbs} {
    margin-top: 12px;
  }

  .overview {
    ${FontStyles.text8};

    margin: 48px auto 40px;
  }

  ${TextField} input {
    max-width: 443px;
  }

  .description {
    margin-bottom: 64px;

    label {
      margin: 0 0 12px 0;

      span {
        margin-right: 8px;
      }
    }

    textarea {
      ${FontStyles.text8};

      max-width: 886px;
      height: 426px;
    }
  }
`;

const spStyle = CustomMedia.lessThan('medium')`
  padding: 8px 16px 48px;

  .overview {
    ${FontStyles.text6};

    margin-bottom: 29px;
  }

  ${TextField} {
    padding: 0;

    input {
      max-width: 100%;
    }
  }

  .description {
    margin-bottom: 32px;

    label {
      margin: 0 0 12px 0;

      span {
        margin-right: 4px;
      }
    }

    textarea {
      ${FontStyles.text6};

      height: 232px;
    }
  }

  .submit-btn-wrapper {
    margin-bottom: 32px;

    ${Button} {
      margin: 0;
    }
  }
`;

export default applyStyles(Contact)(commonStyle, pcStyle, spStyle);



// WEBPACK FOOTER //
// ./src/components/Contact/index.js